import cn from 'classnames'
import Processing from 'ext-ui/components/Processing/Processing'
import { useCursorExpandingCircle } from 'ext-ui/hooks/clickEffects'
import React, {
  ComponentProps,
  forwardRef,
  ReactElement,
  useCallback,
} from 'react'

import style from './Button.style'

type ButtonProps = {
  className?: string
  variant?:
    | 'primary'
    | 'secondary'
    | 'severe'
    | 'spotlight'
    | 'toolbar'
    | 'no-shell'
    | 'no-shell-no-padding'
    | 'no-border'
  loading?: boolean
  iconLeft?: ReactElement
  iconRight?: ReactElement
  dropShadow?: boolean
  testId?: string
  'data-testid'?: string
} & ComponentProps<'button'>

const CIRCLE_RADIUS = 20

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, forwardedRef): ReactElement {
    const {
      className = '',
      variant = 'primary',
      disabled,
      loading,
      iconLeft,
      iconRight,
      dropShadow = true,
      type = 'button',
      children,
      onClick,
      testId,
      ...rest
    } = props

    const { isAnimating, buttonRef, circleCoordinates, handleClick } =
      useCursorExpandingCircle<HTMLButtonElement>(CIRCLE_RADIUS, onClick)

    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        if (buttonRef && 'current' in buttonRef) {
          /* eslint-disable-next-line no-extra-semi */
          ;(
            buttonRef as React.MutableRefObject<HTMLButtonElement | null>
          ).current = node
        }
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else if (forwardedRef) {
          /* eslint-disable-next-line no-extra-semi */
          ;(
            forwardedRef as React.MutableRefObject<HTMLButtonElement | null>
          ).current = node
        }
      },
      [buttonRef, forwardedRef],
    )

    const dataTestId = props['data-testid'] ?? testId ?? 'button'

    return (
      <button
        className={cn('Button', className, variant, {
          disabled,
          loading,
          dropShadow,
        })}
        css={style}
        {...rest}
        ref={setRefs}
        type={type}
        disabled={disabled || loading}
        onClick={handleClick}
        data-testid={dataTestId}
      >
        <div
          className={cn('ripple-effect', variant, {
            isAnimating,
          })}
          style={{
            left: circleCoordinates.x,
            top: circleCoordinates.y,
            height: CIRCLE_RADIUS * 2,
            width: CIRCLE_RADIUS * 2,
          }}
        />
        {loading && <Processing />}
        {!loading && iconLeft}
        {children}
        {iconRight}
      </button>
    )
  },
)

export default Button
